import React, { useEffect, useState, Component } from 'react';
import { ChevronLeftIcon, LockIcon, BookOpenIcon, Loader2Icon, SearchIcon, SparklesIcon } from 'lucide-react';
import { useStory } from './StoryContext';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from './SEOHead';
import { TemplateGallery } from './TemplateGallery';
type StoryGardenProps = {
  onBack: () => void;
};
type APIBook = {
  book_id: string;
  id: string;
  title: string;
  date_created: string;
  cover_url?: string;
  complete?: number;
};
export const StoryGarden = () => {
  const navigate = useNavigate();
  const {
    savedStories,
    isLoggedIn,
    setStoryCount
  } = useStory();
  const [apiBooks, setApiBooks] = useState<APIBook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [communityStories, setCommunityStories] = useState<APIBook[]>([]);
  const [isCommunityLoading, setIsCommunityLoading] = useState(false);
  const [communityError, setCommunityError] = useState<string | null>(null);
  const [localStoryCount, setLocalStoryCount] = useState<number>(0);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [showDebugResponse, setShowDebugResponse] = useState(true);
  const [FEATURED_BOOK_IDS, setFEATURED_BOOK_IDS] = useState(['story_1234', 'story_5678', 'story_9012', '2ba0ef08-6006-4d2b-bf70-e113c221d4d2', 'b62ae0a7-4b1c-4900-8d78-e7be2015e5fb', '82db2c10-cfd7-471c-bc49-a5a8894d5e54', 'fa9ec638-3f8c-401e-ba37-bbe4845284d0', 'a88c3618-4658-401a-b811-8028ca9f6903', 'ea503033-efc9-4f13-8606-c12bda947169']);
  // Batch display variables
  const BATCH_SIZE = 20;
  const [displayCount, setDisplayCount] = useState(BATCH_SIZE);
  const [debugSections, setDebugSections] = useState<{
    yourStories: boolean;
    handpicked: boolean;
    community: boolean;
  }>({
    yourStories: false,
    handpicked: false,
    community: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMethod, setSortMethod] = useState<'read_count' | 'remix_count' | 'date' | 'alpha'>('read_count');
  const toggleCardExpansion = (bookId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookId)) {
        newSet.delete(bookId);
      } else {
        newSet.add(bookId);
      }
      return newSet;
    });
  };
  const toggleDebugSection = (section: keyof typeof debugSections) => {
    setDebugSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  // personal count - now updates the global state through StoryContext
  useEffect(() => {
    const fetchStoryCount = async () => {
      try {
        setIsCountLoading(true);
        setCountError(null);
        const userEmail = localStorage.getItem('user_email');
        if (!userEmail) return;
        const response = await fetch(`https://storybunny.messagesimproved.com/get_story_generation_count?email=${encodeURIComponent(userEmail)}`, {
          method: 'GET'
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const count = data.remaining_book_creations || 0;
        setLocalStoryCount(count);
        // Update the global state in StoryContext
        setStoryCount(count);
      } catch (error) {
        console.error('Error fetching story count:', error);
        setCountError(error instanceof Error ? error.message : 'Failed to fetch story count');
      } finally {
        setIsCountLoading(false);
      }
    };
    if (isLoggedIn) {
      fetchStoryCount();
    }
  }, [isLoggedIn, setStoryCount]);
  // personal library
  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const accessToken = localStorage.getItem('access_token');
        const userEmail = localStorage.getItem('user_email');
        if (!accessToken || !userEmail) {
          console.log('No access token or email found');
          return;
        }
        const response = await fetch(`https://storybunny.messagesimproved.com/get_storybunny_library?email=${encodeURIComponent(userEmail)}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setApiBooks(data.book_list || []);
      } catch (error) {
        console.error('Error fetching library:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch library');
      } finally {
        setIsLoading(false);
      }
    };
    if (isLoggedIn) {
      fetchLibrary();
    }
  }, [isLoggedIn]);
  // Community library - fetch all stories at once
  useEffect(() => {
    const fetchCommunityStories = async () => {
      try {
        setIsCommunityLoading(true);
        setCommunityError(null);
        const response = await fetch('https://storybunny.messagesimproved.com/get_storybunny_library', {
          method: 'GET'
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCommunityStories(data.book_list || []);
      } catch (error) {
        console.error('Error fetching community stories:', error);
        setCommunityError(error instanceof Error ? error.message : 'Failed to fetch community stories');
      } finally {
        setIsCommunityLoading(false);
      }
    };
    fetchCommunityStories();
  }, []);
  // Function to load more community stories
  const loadMoreCommunityStories = () => {
    setDisplayCount(prevCount => prevCount + BATCH_SIZE);
  };
  // Filter and sort the community stories
  const filteredAndSortedCommunityStories = communityStories.filter(book => book.title && book.title.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => {
    if (sortMethod === 'alpha') {
      return (a.title || '').localeCompare(b.title || '');
    } else if (sortMethod === 'read_count') {
      // First compare by read_count (descending)
      const readCountA = a.read_count as number || 0;
      const readCountB = b.read_count as number || 0;
      if (readCountA !== readCountB) {
        return readCountB - readCountA; // Descending order
      }
      // If read counts are equal, fall back to date sorting
      if (!a.date_created) return 1;
      if (!b.date_created) return -1;
      try {
        return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
      } catch (error) {
        console.error('Date sorting error:', error);
        return 0;
      }
    } else if (sortMethod === 'remix_count') {
      // Compare by remix_count (descending)
      const remixCountA = a.remix_count as number || 0;
      const remixCountB = b.remix_count as number || 0;
      if (remixCountA !== remixCountB) {
        return remixCountB - remixCountA; // Descending order
      }
      // If remix counts are equal, fall back to read count sorting (descending)
      const readCountA = a.read_count as number || 0;
      const readCountB = b.read_count as number || 0;
      if (readCountA !== readCountB) {
        return readCountB - readCountA; // Descending order for reads as tiebreaker
      }
      // If both remix and read counts are equal, fall back to date sorting
      if (!a.date_created) return 1;
      if (!b.date_created) return -1;
      try {
        return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
      } catch (error) {
        console.error('Date sorting error:', error);
        return 0;
      }
    } else {
      // Default date sorting
      if (!a.date_created) return 1;
      if (!b.date_created) return -1;
      try {
        return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
      } catch (error) {
        console.error('Date sorting error:', error);
        return 0;
      }
    }
  });
  // Get the current batch of stories to display
  const visibleCommunityStories = filteredAndSortedCommunityStories.slice(0, displayCount);
  // Check if there are more stories to load
  const hasMoreStories = filteredAndSortedCommunityStories.length > displayCount;
  // Handler for navigating to a story with state to indicate the transition source
  const handleNavigateToStory = (bookId: string) => {
    navigate(`/story?book_id=${bookId}`, {
      state: {
        from: 'garden'
      }
    });
  };
  return <>
      <SEOHead title="Story Garden - Your Magical Story Library" description="Discover and explore beautiful AI-created stories in the Story Garden. Browse community stories and manage your personal collection of magical children's books." canonical="https://storybunny.ai/garden" data-id="element-951" />
      <div className="flex-1 flex flex-col bg-[#FFFBF6] overflow-hidden" data-id="element-952">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-[#E5C1A9] p-3" data-id="element-953">
          <div className="max-w-7xl mx-auto" data-id="element-954">
            <div data-id="element-955">
              <h1 className="text-xl font-bold text-[#6B4B3E]" data-id="element-956">Story Garden</h1>
              <p className="text-xs text-[#6B4B3E]/75" data-id="element-957">
                Discover magical stories created using Story Bunny
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-2 md:p-4 overflow-auto" data-id="element-958">
          <div className="max-w-7xl mx-auto space-y-4 md:space-y-8" data-id="element-959">
            {/* Stories Available Section */}
            {isLoggedIn && <div data-id="element-960">
                <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] mb-3 px-2" data-id="element-961">
                  Stories Available
                </h2>
                <div className="bg-[#FFF0E6] rounded-lg md:rounded-xl p-4" data-id="element-962">
                  {isCountLoading ? <div className="flex items-center justify-center h-12" data-id="element-963">
                      <Loader2Icon className="w-6 h-6 animate-spin text-[#D4A088]" data-id="element-964" />
                    </div> : countError ? <div className="text-center text-red-600 text-sm" data-id="element-965">
                      {countError}
                    </div> : <div className="flex flex-col items-center space-y-4" data-id="element-966">
                      <p className="text-center text-[#6B4B3E]" data-id="element-967">
                        You have{' '}
                        <span className="font-bold" data-id="element-968">{localStoryCount}</span>{' '}
                        stories remaining to create
                      </p>
                      {localStoryCount > 0 ? <Link to="/" className="inline-flex items-center px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-969">
                          Create Your Next Story
                        </Link> : <Link to="/pricing" className="inline-flex items-center px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-970">
                          Subscribe
                        </Link>}
                    </div>}
                </div>
              </div>}

            {/* Your Stories Section */}
            <div data-id="element-971">
              <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] mb-3 px-2 cursor-pointer" onDoubleClick={() => toggleDebugSection('yourStories')} data-id="element-972">
                Your Stories {apiBooks.length > 0 && `(${apiBooks.length})`}
              </h2>
              {!isLoggedIn ? <div className="bg-[#FFF0E6] rounded-lg md:rounded-xl p-4 text-center" data-id="element-973">
                  <div className="flex flex-col items-center space-y-3" data-id="element-974">
                    <LockIcon size={36} className="text-[#D4A088]" data-id="element-975" />
                    <div data-id="element-976">
                      <h3 className="text-base md:text-lg font-bold text-[#6B4B3E] mb-1" data-id="element-977">
                        Create an Account to Save Your Stories
                      </h3>
                      <p className="text-sm md:text-base text-[#6B4B3E]/75 mb-3" data-id="element-978">
                        Keep all your magical stories in one place
                      </p>
                      <Link to="/create-account" className="inline-block px-4 py-1.5 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors text-sm" data-id="element-979">
                        Create Account
                      </Link>
                    </div>
                  </div>
                </div> : isLoading ? <div className="flex items-center justify-center h-32" data-id="element-980">
                  <Loader2Icon className="w-8 h-8 animate-spin text-[#D4A088]" data-id="element-981" />
                </div> : error ? <div className="bg-red-50 rounded-lg md:rounded-xl p-4 text-center text-red-600" data-id="element-982">
                  <p data-id="element-983">Error loading stories: {error}</p>
                </div> : apiBooks.length === 0 ? <div className="bg-[#FFF0E6] rounded-lg md:rounded-xl p-4 text-center" data-id="element-984">
                  <div className="flex flex-col items-center space-y-4" data-id="element-985">
                    <BookOpenIcon size={36} className="text-[#D4A088]" data-id="element-986" />
                    <div data-id="element-987">
                      <h3 className="text-base md:text-lg font-bold text-[#6B4B3E] mb-1" data-id="element-988">
                        No Stories Yet
                      </h3>
                      <p className="text-sm md:text-base text-[#6B4B3E]/75 mb-4" data-id="element-989">
                        Your magical stories will appear here once you create
                        them
                      </p>
                      <Link to="/" className="inline-flex items-center justify-center px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors text-sm md:text-base" data-id="element-990">
                        Create Your First Story For Free
                      </Link>
                    </div>
                  </div>
                </div> : <div className="overflow-x-auto -mx-2 px-2 pb-4" data-id="element-991">
                  <div className="flex gap-3 pl-2 pr-4 overflow-x-auto snap-x" data-id="element-992">
                    {apiBooks.sort((a, b) => {
                  const dateA = new Date(a.date_created || Date.now());
                  const dateB = new Date(b.date_created || Date.now());
                  return dateB.getTime() - dateA.getTime();
                }).map(book => <div key={book.book_id} onClick={() => handleNavigateToStory(book.id)} className="flex-shrink-0 w-[100px] sm:w-[120px] md:w-[140px] bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 snap-start" data-id="element-993">
                          {debugSections.yourStories ? <div className="p-2" data-id="element-994">
                              <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words text-[#6B4B3E]/75" data-id="element-995">
                                {JSON.stringify(book, null, 2)}
                              </pre>
                            </div> : <>
                              {/* cover */}
                              <div className="aspect-[3/4] relative" data-id="element-996">
                                <img src={book.cover_url ? book.cover_url.startsWith('http') ? book.cover_url : `https://${book.cover_url}` : 'https://uploadthingy.s3.us-west-1.amazonaws.com/dgYPmgQZXQdrKaC8NJmmVJ/ChatGPT_Image_May_18%2C_2025_at_09_37_05_AM.png'} alt={book.title} className="w-full h-full object-cover" onError={e => {
                        e.currentTarget.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/dgYPmgQZXQdrKaC8NJmmVJ/ChatGPT_Image_May_18%2C_2025_at_09_37_05_AM.png';
                      }} data-id="element-997" />
                              </div>
                              <div className="p-2" data-id="element-998">
                                {/* title */}
                                <h3 className="text-sm font-medium text-[#6B4B3E] line-clamp-2" data-id="element-999">
                                  {book.title}
                                </h3>
                                {/* date */}
                                <p className="text-xs text-[#6B4B3E]/60 mt-1" data-id="element-1000">
                                  {(() => {
                          try {
                            const date = book.date_created;
                            if (!date) return '';
                            return new Date(date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            });
                          } catch (error) {
                            return '';
                          }
                        })()}
                                </p>
                              </div>
                            </>}
                        </div>)}
                  </div>
                </div>}
            </div>

            {/* Made With Story Bunny Section */}
            <div data-id="element-1001">
              <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] mb-3 px-2" onDoubleClick={() => toggleDebugSection('community')} data-id="element-1002">
                The Community Garden
              </h2>
              <div className="bg-[#FFF0E6] rounded-lg md:rounded-xl p-2 md:p-4" data-id="element-1003">
                {/* Search and Sort Controls */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4" data-id="element-1004">
                  <div className="relative flex-1" data-id="element-1005">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B4B3E]/60 w-4 h-4" data-id="element-1006" />
                    <input type="text" placeholder="Search stories by title..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-[#E5C1A9] focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1007" />
                  </div>
                  <select value={sortMethod} onChange={e => setSortMethod(e.target.value as 'read_count' | 'remix_count' | 'date' | 'alpha')} className="px-4 py-2 rounded-lg border border-[#E5C1A9] focus:outline-none focus:border-[#D4A088] text-[#6B4B3E] bg-white" data-id="element-1008">
                    <option value="read_count" data-id="element-1009">Sort by Reads</option>
                    <option value="remix_count" data-id="element-1010">Sort by Remixes</option>
                    <option value="date" data-id="element-1011">Sort by Date (Newest First)</option>
                    <option value="alpha" data-id="element-1012">Sort Alphabetically</option>
                  </select>
                </div>
                {/* Loading state for initial load */}
                {isCommunityLoading ? <div className="flex items-center justify-center h-32" data-id="element-1013">
                    <Loader2Icon className="w-8 h-8 animate-spin text-[#D4A088]" data-id="element-1014" />
                  </div> : communityError ? <div className="bg-red-50 rounded-lg p-4 text-center text-red-600" data-id="element-1015">
                    <p data-id="element-1016">Error loading community stories: {communityError}</p>
                  </div> : <>
                    {/* Book Display - Only showing the current batch */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 md:gap-4" data-id="element-1017">
                      {visibleCommunityStories.map(book => <div key={book.book_id || book.id} className="flex-shrink-0 w-full bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 cursor-pointer" onClick={() => handleNavigateToStory(book.id)} data-id="element-1018">
                          {debugSections.community ? <div className="p-2" data-id="element-1019">
                              <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words text-[#6B4B3E]/75" data-id="element-1020">
                                {JSON.stringify(book, null, 2)}
                              </pre>
                            </div> : <>
                              <div className="aspect-[3/4] relative" data-id="element-1021">
                                <img key={`${book.id}-${book.title}`} src={book.cover_url?.startsWith('http') ? book.cover_url : book.cover_url ? `https://${book.cover_url}` : 'https://uploadthingy.s3.us-west-1.amazonaws.com/dgYPmgQZXQdrKaC8NJmmVJ/ChatGPT_Image_May_18%2C_2025_at_09_37_05_AM.png'} alt={book.title} className="w-full h-full object-cover" onError={e => {
                          e.currentTarget.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/dgYPmgQZXQdrKaC8NJmmVJ/ChatGPT_Image_May_18%2C_2025_at_09_37_05_AM.png';
                        }} data-id="element-1022" />
                              </div>
                              <div className="p-2 flex flex-col h-[120px]" data-id="element-1023">
                                <h3 className="text-sm font-medium text-[#6B4B3E] line-clamp-2" data-id="element-1024">
                                  {book.title}
                                </h3>
                                <p className="text-xs text-[#6B4B3E]/60 mt-1" data-id="element-1025">
                                  {(() => {
                            try {
                              const date = book.date_created;
                              if (!date) return 'Featured';
                              return new Date(date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              });
                            } catch (error) {
                              return 'Featured';
                            }
                          })()}
                                </p>
                                <div className="flex-grow" data-id="element-1026"></div>
                                {/* Read count display */}
                                <p className="text-xs text-[#6B4B3E]/60 mt-1 flex items-center" data-id="element-1027">
                                  <BookOpenIcon className="w-3 h-3 mr-1 inline" data-id="element-1028" />
                                  {book.read_count || 0}{' '}
                                  {book.read_count === 1 ? 'read' : 'reads'}
                                </p>
                                {/* Remix count display - only shown when > 0 */}
                                {book.remix_count > 0 && <p className="text-xs text-[#6B4B3E]/60 mt-1 flex items-center" data-id="element-1029">
                                    <SparklesIcon className="w-3 h-3 mr-1 inline" data-id="element-1030" />
                                    {book.remix_count}{' '}
                                    {book.remix_count === 1 ? 'remix' : 'remixes'}
                                  </p>}
                              </div>
                            </>}
                        </div>)}
                    </div>
                    {/* Load More Button - Only shown if there are more stories to load */}
                    {hasMoreStories && <div className="flex justify-center mt-6" data-id="element-1031">
                        <button onClick={loadMoreCommunityStories} className="px-6 py-3 bg-[#E5C1A9] text-[#6B4B3E] rounded-full hover:bg-[#D4A088] hover:text-white transition-colors" data-id="element-1032">
                          Load More Stories
                        </button>
                      </div>}
                    {/* Show count information */}
                    <div className="text-center text-[#6B4B3E]/60 text-xs mt-4" data-id="element-1033">
                      Showing {visibleCommunityStories.length} of{' '}
                      {filteredAndSortedCommunityStories.length} stories
                    </div>
                  </>}
              </div>
            </div>

            {/* Template Gallery Section */}
            <TemplateGallery data-id="element-1034" />

            {/* Handpicked by Story Bunny */}
            {/*
             <div>
             <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] mb-3 px-2">
             Handpicked by Story Bunny
             </h2>
             <div className="overflow-x-auto -mx-2 px-2 pb-4">
             <div className="flex gap-3 pl-2 pr-4 overflow-x-auto snap-x">
             {communityStories
             .filter((book) => FEATURED_BOOK_IDS.includes(book.id))
             .map((book) => (
             <div
             key={book.book_id}
             className="flex-shrink-0 w-[100px] sm:w-[120px] md:w-[140px] bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 snap-start"
             onClick={() => navigate(`/story?book_id=${book.id}`)}
             >
             <div className="aspect-[3/4] relative">
             <img
             src={
             book.cover_url?.startsWith('http')
             ? book.cover_url
             : book.cover_url
              ? `https://${book.cover_url}`
              : '/bunny_face.png'
             }
             alt={book.title}
             className="w-full h-full object-cover"
             onError={(e) => {
             e.currentTarget.src = '/bunny_face.png'
             }}
             />
             </div>
             <div className="p-2">
             <h3 className="text-sm font-medium text-[#6B4B3E] line-clamp-2">
             {book.title}
             </h3>
             <p className="text-xs text-[#6B4B3E]/60 mt-1">
             {(() => {
             try {
             const date = book.date_created
             if (!date) return 'Featured Story'
             return new Date(date).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
              },
             )
             } catch (error) {
             return 'Featured Story'
             }
             })()}
             </p>
             </div>
             </div>
             ))}
             </div>
             </div>
             </div>
             */}
          </div>
        </div>
      </div>
    </>;
};